import React from 'react';
import { Button, Dropdown } from 'antd';
import { ColumnsType } from 'antd/lib/table';
import { Image, InventoryCount } from 'common/components';
import { countFormatter, dateFormatter, mapToMenuWithDivider } from 'common/helpers';
import { DEFAULT_VALUE_0 } from 'common/config';
import { IInventoryActionShort, IInventoryAssigneeShort, IOption, ISubdivisionShort } from 'common/models';
import { Affiliate, ArrowRightLong, History, LinkedUser, Options, User } from 'common/svg';
import { getUserName, getUserOptions, getUserRole } from 'entities/User/User.helper';
import { IUser, IUserInventoryStats } from 'entities/User/User.models';
import {
  EInventoryActionReason,
  EInventoryActionReasonLabel,
  EInventoryActionStatus,
  EInventoryActionType,
  EInventoryStatus,
} from 'entities/Inventory/Inventory.enum';
import { IInventory } from 'entities/Inventory/Inventory.models';
import { getSubdivisionOptions } from 'entities/Subdivision/Subdivision.helper';
import { IInventoryAction } from 'entities/InventoryAction/InventoryAction.models';

export const inventoryCount = {
  totalCount: (inventory: IInventory | null) => {
    if (!inventory) {
      return DEFAULT_VALUE_0;
    }

    return inventory.count;
  },
  transferRequestsCount: (inventory: IInventory | null) => {
    if (!inventory) {
      return DEFAULT_VALUE_0;
    }

    return inventory.actions.reduce((acc, action) => {
      if (action.status === EInventoryActionStatus.New && action.type === EInventoryActionType.Transfer) {
        return acc + action.count;
      }

      return acc;
    }, DEFAULT_VALUE_0);
  },
  writeOffRequestCount: (inventory: IInventory | null) => {
    if (!inventory) {
      return DEFAULT_VALUE_0;
    }

    return inventory.actions.reduce((acc, action) => {
      if (action.status === EInventoryActionStatus.New && action.type === EInventoryActionType.WriteOff) {
        return acc + action.count;
      }

      return acc;
    }, DEFAULT_VALUE_0);
  },
  withoutTransferRequestsCount: (inventory: IInventory | null) => {
    if (!inventory) {
      return DEFAULT_VALUE_0;
    }

    const transferRequestsCount = inventory.actions.reduce((acc, action) => {
      if (action.status === EInventoryActionStatus.New && action.type === EInventoryActionType.Transfer) {
        return acc + action.count;
      }

      return acc;
    }, DEFAULT_VALUE_0);

    return inventory.count - transferRequestsCount;
  },
  withoutWriteOffRequestsCount: (inventory: IInventory | null) => {
    if (!inventory) {
      return DEFAULT_VALUE_0;
    }

    const writeOffRequestCount = inventory.actions.reduce((acc, action) => {
      if (action.status === EInventoryActionStatus.New && action.type === EInventoryActionType.WriteOff) {
        return acc + action.count;
      }

      return acc;
    }, DEFAULT_VALUE_0);

    return inventory.count - writeOffRequestCount;
  },
  availableCount: (inventory: IInventory | null) => {
    if (!inventory) {
      return DEFAULT_VALUE_0;
    }

    const newRequestCount = inventory.actions.reduce((acc, action) => {
      if (action.status === EInventoryActionStatus.New) {
        return acc + action.count;
      }

      return acc;
    }, DEFAULT_VALUE_0);
    const assignedCount = inventory.assignees.reduce((acc, assignee) => {
      return acc + assignee.count;
    }, DEFAULT_VALUE_0);

    return inventory.count - newRequestCount - assignedCount;
  },
};

export const mapInventoryActionToInventoryActionShort = (action: IInventoryAction) => {
  return {
    id: action.id,
    createdAt: action.createdAt,
    updatedAt: action.updatedAt,
    count: action.count,
    type: action.type,
    status: action.status,
    good: action.good,
  };
};

export const getInventoryTransferCardSubdivisionOptions = (list: ISubdivisionShort[]) => {
  const options = getSubdivisionOptions(list);
  return mapToMenuWithDivider(options);
};

export const getInventoryTransferCardUserOptions = (userList: IUser[], inventoryUserId?: number, currentUserId?: number) => {
  const filteredList = userList.filter((user) => user.id !== inventoryUserId && user.id !== currentUserId);
  const options = getUserOptions(filteredList);
  return mapToMenuWithDivider(options);
};

export const getInventoryCardNotificationMessage = (inventory: IInventory | null, action: IInventoryActionShort) => {
  let message = '';

  if (inventory?.user) {
    message += `Отправитель: ${getUserName.fullLastNameAndInitials(
      inventory.user.lastName,
      inventory.user.firstName,
      inventory.user.patronymic,
    )} `;
  }

  if (action.type === EInventoryActionType.Transfer) {
    if (action.receiver) {
      message += `Новый ответственный: ${getUserName.fullLastNameAndInitials(
        action.receiver.lastName,
        action.receiver.firstName,
        action.receiver.patronymic,
      )} `;
    }

    message += `Количество оборудования: ${countFormatter(action.count)}`;
  } else {
    if (action.reason) {
      message += `Причина: «${EInventoryActionReasonLabel[action.reason]}». `;
    }

    if (action.comment) {
      message += `Комментарий: «${action.comment}»`;
    }
  }

  return message;
};

export const getInventoryActionTransferNotificationDescription = (inventoryAction: IInventoryAction) => {
  let description = '';

  if (inventoryAction.user) {
    description += `Отправитель: ${getUserName.fullLastNameAndInitials(
      inventoryAction.user?.lastName,
      inventoryAction.user?.firstName,
      inventoryAction.user?.patronymic,
    )} `;
  }

  if (inventoryAction.receiver) {
    description += `Новый ответственный: ${getUserName.fullLastNameAndInitials(
      inventoryAction.receiver?.lastName,
      inventoryAction.receiver?.firstName,
      inventoryAction.receiver?.patronymic,
    )} `;
  }

  description += `Количество оборудования: ${countFormatter(inventoryAction.count)}`;

  return description;
};

export const getInventoryActionWriteOffNotificationDescription = (inventoryAction: IInventoryAction) => {
  let description = '';

  if (inventoryAction.user) {
    description += `Отправитель: ${getUserName.fullLastNameAndInitials(
      inventoryAction.user?.lastName,
      inventoryAction.user?.firstName,
      inventoryAction.user?.patronymic,
    )} `;
  }

  if (inventoryAction.reason) {
    description += `Причина: ${EInventoryActionReasonLabel[inventoryAction.reason]}. `;
  }

  if (inventoryAction.comment) {
    description += `Комментарий: ${inventoryAction.comment}. `;
  }

  description += `Количество оборудования: ${countFormatter(inventoryAction.count)}`;

  return description;
};

export const getInventoryAssigneeOptions = (assignees: IInventoryAssigneeShort[], search: string): IOption[] => {
  const options = assignees
    .filter((assignee) => assignee.name.toLowerCase().includes(search.toLowerCase()))
    .map((assignee) => ({ label: assignee.name, value: assignee.id }));

  return mapToMenuWithDivider(options);
};

export const getWriteOffReasonOptions = () => {
  return mapToMenuWithDivider([
    { label: EInventoryActionReasonLabel.tool_loss, value: EInventoryActionReason.ToolLoss },
    { label: EInventoryActionReasonLabel.by_inventory, value: EInventoryActionReason.ByInventory },
    { label: EInventoryActionReasonLabel.other, value: EInventoryActionReason.Other },
  ]);
};

export const getInventoriesTabsCounts = (
  user: IUser | null,
  stats: IUserInventoryStats,
): { totalInventoriesCount: number; totalTransferActionsCount: number; totalWriteOffActionsCount: number } => {
  const { isAccountAdmin, isHeadOfDepartment } = getUserRole(user?.roles);
  const { inventoryAvailableCount, inventoryWrittenOffCount, incomingTransfersCount, outcomingTransfersCount, writeOffsCount } =
    stats;

  return {
    totalInventoriesCount:
      isAccountAdmin || isHeadOfDepartment
        ? inventoryAvailableCount + inventoryWrittenOffCount
        : inventoryAvailableCount + inventoryWrittenOffCount + writeOffsCount,
    totalTransferActionsCount: incomingTransfersCount + outcomingTransfersCount,
    totalWriteOffActionsCount: writeOffsCount,
  };
};

export const renderInventoryListRecords = (params?: {
  isAvailable: boolean;
  onTransferClick: (id: number) => void;
  onWriteOffClick: (id: number) => void;
  onAssigneeClick: (id: number) => void;
  currentUserId?: number;
}) => {
  const records = [
    {
      key: 'image',
      render: (_, { good }) => <Image size={60} src={good?.images?.[0]} />,
      width: 80,
    },
    {
      key: 'name',
      render: (_, record) => {
        const writeOffRequestCount = inventoryCount.writeOffRequestCount(record);
        const transferRequestCount = inventoryCount.transferRequestsCount(record);

        return (
          <>
            <div className="ff-medium fs-s ellipsis-2 mb-2">{record.good?.name}</div>

            <div className="flex align-center wrap" style={{ columnGap: 20 }}>
              {record.user && record.status === EInventoryStatus.Available && (
                <div className="flex align-center">
                  <User className="icon-user-dark-grey mr-3" />

                  <span className="ff-medium fs-xs color-dark-grey">
                    Ответственный:{' '}
                    {getUserName.fullLastNameAndInitials(record.user?.lastName, record.user?.firstName, record.user?.patronymic)}
                  </span>
                </div>
              )}

              {record.subdivision && params?.isAvailable && (
                <div className="flex align-center">
                  <div className="mr-3 flex align-center">
                    <Affiliate className="icon-affiliate-dark-grey" />
                  </div>

                  <span className="ff-medium fs-xs color-dark-grey">{`Подразделение: ${record.subdivision.name}`}</span>
                </div>
              )}

              {!!record.assignees.length &&
                record.assignees.map((assignee) => {
                  return (
                    <div key={assignee.id} className="flex align-center">
                      <LinkedUser className="icon-linked-user-dark-grey mr-3" />

                      <span className="ff-medium fs-xs color-dark-grey">
                        {`Закреплено: ${assignee.name}, ${countFormatter(assignee.count)}`}
                      </span>
                    </div>
                  );
                })}

              {record.status === EInventoryStatus.WriteOff && (
                <div className="flex align-center">
                  <ArrowRightLong className="icon-arrow-right-long-dark-grey mr-3" />

                  <span className="ff-medium fs-xs color-dark-grey">Списан в количестве {countFormatter(record.count)}</span>
                </div>
              )}

              <div className="flex align-center">
                <History className="icon-history-dark-grey mr-3" />

                <span className="ff-medium fs-xs color-dark-grey">Обновлено {dateFormatter('DD.MM.YYYY', record.updatedAt)}</span>
              </div>

              {!!transferRequestCount && (
                <div className="flex align-center">
                  <ArrowRightLong className="icon-arrow-right-long-dark-grey mr-3" />

                  <span className="ff-medium fs-xs color-dark-grey">
                    Запрос о передаче {countFormatter(transferRequestCount)}
                  </span>
                </div>
              )}

              {!!writeOffRequestCount && (
                <div className="flex align-center">
                  <ArrowRightLong className="icon-arrow-right-long-dark-grey mr-3" />

                  <span className="ff-medium fs-xs color-dark-grey">
                    Запрос о списании {countFormatter(writeOffRequestCount)}
                  </span>
                </div>
              )}

              {record.good?.sellerCode && <div className="ff-medium fs-xs color-dark-grey mr-6">{record.good.sellerCode}</div>}

              {record.good?.buyerCode && <span className="ff-medium fs-xs color-bright-green">{record.good.buyerCode}</span>}
            </div>
          </>
        );
      },
    },
    {
      key: 'count',
      render: (_, record) => {
        return (
          <div className="flex align-center justify-end">
            <InventoryCount count={record.count} />

            <Dropdown
              overlayClassName="redesign"
              menu={{
                items: [
                  {
                    key: 'hand-over',
                    label: 'Передать инструмент',
                    onClick: ({ domEvent }) => {
                      domEvent.stopPropagation();
                      params?.onTransferClick(record.id);
                    },
                    disabled: record.user && record.user.id !== params?.currentUserId,
                  },
                  { type: 'divider' },
                  {
                    key: 'assign',
                    label: 'Закрепить за сотрудником',
                    onClick: ({ domEvent }) => {
                      domEvent.stopPropagation();
                      params?.onAssigneeClick(record.id);
                    },
                    disabled: record.user && record.user.id !== params?.currentUserId,
                  },
                  { type: 'divider' },
                  {
                    key: 'send',
                    label: 'Отправить на списание',
                    onClick: ({ domEvent }) => {
                      domEvent.stopPropagation();
                      params?.onWriteOffClick(record.id);
                    },
                    className: 'red',
                  },
                ],
              }}
              placement="bottomRight"
            >
              <Button
                style={{ marginLeft: 16 }}
                className="button-icon"
                icon={<Options className="icon-options-dark-grey" style={{ transform: 'rotate(90deg)' }} />}
              />
            </Dropdown>
          </div>
        );
      },
      hidden: !params?.isAvailable,
    },
    {
      key: 'empty',
      hidden: params?.isAvailable,
    },
  ] as ColumnsType<IInventory>;

  // @ts-ignore
  return records.filter((record) => !record.hidden);
};

export const renderInventoryActionListRecords = (actions?: {
  isIncoming?: boolean;
  onAcceptRequestClick?: (records: IInventoryAction[]) => void;
  onCancelRequestClick?: (records: IInventoryAction[]) => void;
}) => {
  const records = [
    {
      key: 'image',
      render: (_, { good: { images } }) => <Image size={60} src={images[0]} />,
      width: 80,
    },
    {
      key: 'name',
      render: (_, record) => {
        const isWriteOffInventoryAction = record.type === EInventoryActionType.WriteOff;

        return (
          <>
            <div className="ff-medium fs-s ellipsis-2 mb-2">{record.good.name}</div>

            <div className="flex align-center wrap" style={{ columnGap: 20 }}>
              {record.user && (
                <div className="flex align-center">
                  <User className="icon-user-dark-grey mr-3" />

                  <span className="ff-medium fs-xs color-dark-grey">
                    Ответственный:{' '}
                    {getUserName.fullLastNameAndInitials(record.user?.lastName, record.user?.firstName, record.user?.patronymic)}
                  </span>
                </div>
              )}

              {record.subdivision && (
                <div className="flex align-center">
                  <div className="mr-3 flex align-center">
                    <Affiliate className="icon-affiliate-dark-grey" />
                  </div>

                  <span className="ff-medium fs-xs color-dark-grey">{`Подразделение: ${record.subdivision.name}`}</span>
                </div>
              )}

              {isWriteOffInventoryAction ? (
                <div className="flex align-center">
                  <ArrowRightLong className="icon-arrow-right-long-dark-grey mr-3" />

                  <span className="text-tag-accent color-dark-grey">Запрос о списании {countFormatter(record.count)}</span>
                </div>
              ) : (
                <div className="flex align-center">
                  <History className="icon-history-dark-grey mr-3" />

                  <span className="ff-medium fs-xs color-dark-grey">Запрос о передаче {countFormatter(record.count)}</span>
                </div>
              )}

              {record.good.sellerCode && <div className="ff-medium fs-xs color-dark-grey mr-6">{record.good.sellerCode}</div>}

              {record.good.buyerCode && <span className="ff-medium fs-xs color-bright-green">{record.good.buyerCode}</span>}
            </div>
          </>
        );
      },
    },
    {
      key: 'actions',
      render: (_, record) => {
        return (
          <div className="flex align-center justify-between">
            <Button
              className="button-sm success"
              onClick={(e) => {
                e.stopPropagation();
                actions?.onAcceptRequestClick?.([record]);
              }}
            >
              Принять запрос
            </Button>

            <Button
              className="button-sm secondary"
              onClick={(e) => {
                e.stopPropagation();
                actions?.onCancelRequestClick?.([record]);
              }}
            >
              Отклонить
            </Button>
          </div>
        );
      },
      width: 304,
      hidden: !actions?.isIncoming,
    },
    {
      key: 'actions',
      render: (_, record) => {
        return (
          <Button
            className="button-sm secondary"
            onClick={(e) => {
              e.stopPropagation();
              actions?.onCancelRequestClick?.([record]);
            }}
          >
            Отменить запрос
          </Button>
        );
      },
      width: 178,
      hidden: actions?.isIncoming,
    },
  ] as ColumnsType<IInventoryAction>;

  // @ts-ignore
  return records.filter((record) => !record.hidden);
};
